// TES-277 — THE article-body markdown parser. One grammar, four renderers.
//
// The article-body subset was previously parsed by four hand-rolled,
// diverging implementations (admin RichTextEditor load, admin
// MarkdownPreview, mobile Markdown, web articleMarkdown). This module is now
// the single source of truth for the GRAMMAR; each surface keeps only its
// renderer over the block tree.
//
// Core grammar = exactly what the admin WYSIWYG can author (its ceiling):
//   - ATX headings `# / ## / ###`
//   - `**bold**` inline (the only inline mark)
//   - `- ` / `* ` bullet lists
//   - `- [ ]` / `- [x]` checklists
//   - blank-line-separated paragraphs; soft-wrapped lines are preserved as
//     separate entries in `paragraph.lines` so each renderer chooses its own
//     join (mobile/admin join with a space; web renders <br>).
// Every line is trimmed before matching (the patient-facing mobile renderer
// always did this; indented content must not silently downgrade).
//
// `legacyWebConventions` additionally enables the three conventions the
// TES-190 landing articles (`type: "article"`, rendered by apps/web) rely
// on: a standalone `**bold**` line as a heading, `✓ ` check lines, and a
// `**… →**` line as a CTA. They are OPT-IN because promoting them globally
// would change how existing mobile content renders (a coach message's bold
// line must stay a bold paragraph, not become a heading).
//
// PURITY CONTRACT: no JSX, no React/RN/DOM imports, no dependencies — this
// file is consumed by Metro (mobile), Vite (admin), Astro (web) and Node
// (api) alike. Renderers live in the apps.

export interface ArticleInline {
  text: string;
  bold: boolean;
}

export type ArticleBlock =
  | { kind: "heading"; level: 1 | 2 | 3; content: ArticleInline[] }
  | { kind: "paragraph"; lines: ArticleInline[][] }
  | { kind: "bullets"; items: ArticleInline[][] }
  | {
      kind: "checklist";
      items: { checked: boolean; content: ArticleInline[] }[];
    }
  // Legacy web convention (`**Läs mer →**`) — superseded by the structured
  // Article.cta field, kept so existing `type:"article"` bodies still render.
  | { kind: "cta"; label: string }
  // Media support: `![alt](media:<key>)` on its own line — a
  // tenant MediaAsset reference. The alt here is a FALLBACK; renderers prefer
  // the asset's authored altSv. Unknown keys render as nothing patient-side;
  // import/publish validation is where a dangling reference fails loud.
  | { kind: "image"; mediaKey: string; alt: string };

export interface ParseArticleBodyOptions {
  // Enable the legacy `type:"article"` conventions (standalone-bold heading,
  // ✓ checks, arrow CTA). apps/web passes true; everyone else omits it.
  legacyWebConventions?: boolean;
}

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
// `![alt](media:key)` — media references only; http images are not supported.
const IMAGE_RE = /^!\[([^\]]*)\]\(media:([a-z0-9_]+(?:\.[a-z0-9_]+)*)\)$/;
const TASK_RE = /^[-*]\s+\[([ xX])\]\s+(.*)$/;
const BULLET_RE = /^[-*]\s+(.*)$/;
const BOLD_SPLIT_RE = /(\*\*[^*]+\*\*)/g;
const BOLD_RE = /^\*\*([^*]+)\*\*$/;
// Legacy web: a whole line that is exactly one bold span…
const STANDALONE_BOLD_RE = /^\*\*(.+?)\*\*$/;
// …ending in an arrow marks a CTA rather than a heading.
const TRAILING_ARROW_RE = /(→|➡)\s*$/;
// Space after ✓ optional — the old web parser accepted "✓text" too, and the
// landing bodies are DB-editable so a dropped space must not change render.
const LEGACY_CHECK_RE = /^✓\s*(.*)$/;

// `**bold**` is the only inline mark; everything else is literal text.
export function parseInline(text: string): ArticleInline[] {
  return text
    .split(BOLD_SPLIT_RE)
    .filter((part) => part !== "")
    .map((part) => {
      const m = part.match(BOLD_RE);
      return m ? { text: m[1]!, bold: true } : { text: part, bold: false };
    });
}

// Paragraph inline parsing happens on the JOINED paragraph text, then the
// segments are split back onto their source lines — so a `**bold**` span
// crossing a soft-wrapped line stays bold (the historical admin/mobile
// semantic; parsing per line would render literal asterisks in the admin
// while mobile showed bold — the exact drift this module exists to kill).
// `[^*]` matches \n, so BOLD_SPLIT_RE spans the join naturally.
function parseParagraphLines(rawLines: string[]): ArticleInline[][] {
  const segments = parseInline(rawLines.join("\n"));
  const lines: ArticleInline[][] = [[]];
  for (const seg of segments) {
    const parts = seg.text.split("\n");
    parts.forEach((part, idx) => {
      if (idx > 0) lines.push([]);
      if (part !== "") {
        lines[lines.length - 1]!.push({ text: part, bold: seg.bold });
      }
    });
  }
  return lines.filter((l) => l.length > 0);
}

export function parseArticleBody(
  md: string,
  opts: ParseArticleBodyOptions = {},
): ArticleBlock[] {
  const legacy = opts.legacyWebConventions === true;
  const lines = (md ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.trim());

  const blocks: ArticleBlock[] = [];
  let para: string[] = [];
  // `atBlockBoundary` is true only for blank-line/EOF flushes. The legacy
  // standalone-bold promotion applies ONLY there — the old web parser split
  // on blank lines first, so a bold line directly followed by a list line
  // (no blank between) was one paragraph, never a heading. A flush forced
  // by an adjacent list/heading line must not promote.
  const flushPara = (atBlockBoundary = false) => {
    if (para.length === 0) return;
    if (legacy && atBlockBoundary && para.length === 1) {
      const solo = para[0]!.match(STANDALONE_BOLD_RE);
      if (solo) {
        const inner = solo[1]!;
        if (TRAILING_ARROW_RE.test(inner)) {
          blocks.push({
            kind: "cta",
            label: inner.replace(TRAILING_ARROW_RE, "").trim(),
          });
        } else {
          blocks.push({
            kind: "heading",
            level: 2,
            content: parseInline(inner),
          });
        }
        para = [];
        return;
      }
    }
    blocks.push({ kind: "paragraph", lines: parseParagraphLines(para) });
    para = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line === "") {
      flushPara(true);
      i++;
      continue;
    }

    const image = line.match(IMAGE_RE);
    if (image) {
      flushPara();
      blocks.push({ kind: "image", alt: image[1]!, mediaKey: image[2]! });
      i++;
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      flushPara();
      blocks.push({
        kind: "heading",
        level: heading[1]!.length as 1 | 2 | 3,
        content: parseInline(heading[2]!),
      });
      i++;
      continue;
    }

    // Contiguous same-kind list runs become ONE block (a bullet list
    // followed by a checklist stays two blocks — this grouping is what the
    // RichTextEditor's phantom-empty-task-item fix depends on).
    const isTask = TASK_RE.test(line);
    const isLegacyCheck = legacy && !isTask && LEGACY_CHECK_RE.test(line);
    const isBullet = !isTask && !isLegacyCheck && BULLET_RE.test(line);
    if (isTask || isLegacyCheck) {
      flushPara();
      const items: { checked: boolean; content: ArticleInline[] }[] = [];
      while (i < lines.length) {
        const task = lines[i]!.match(TASK_RE);
        const check = !task && legacy ? lines[i]!.match(LEGACY_CHECK_RE) : null;
        if (isTask && task) {
          items.push({
            checked: task[1]!.toLowerCase() === "x",
            content: parseInline(task[2]!),
          });
          i++;
        } else if (isLegacyCheck && check) {
          // ✓ lines are read-only affirmations — modelled as checked items.
          items.push({ checked: true, content: parseInline(check[1]!) });
          i++;
        } else break;
      }
      blocks.push({ kind: "checklist", items });
      continue;
    }
    if (isBullet) {
      flushPara();
      const items: ArticleInline[][] = [];
      while (i < lines.length) {
        const isTaskRow = TASK_RE.test(lines[i]!);
        const bullet = isTaskRow ? null : lines[i]!.match(BULLET_RE);
        if (bullet) {
          items.push(parseInline(bullet[1]!));
          i++;
        } else break;
      }
      blocks.push({ kind: "bullets", items });
      continue;
    }

    para.push(line);
    i++;
  }
  flushPara(true);
  return blocks;
}

// Convenience for renderers/excerpts that just need the visible text.
export function inlineToPlainText(content: ArticleInline[]): string {
  return content.map((s) => s.text).join("");
}

// TES-367 — grammar lint for authored/imported markdown. The parser above is
// the WHOLE supported grammar (headings, paragraphs, `- ` bullets, `- [ ]`
// checklists, **bold**); anything else renders as literal text on mobile.
// Returns machine-readable codes for the constructs authors reach for most —
// used by the bulk-import warning layer (TES-361) so Codex sees
// `markdown_unsupported:<construct>` instead of shipping literal `[länk](…)`
// to patients. Deliberately conservative: only patterns that are
// unambiguously markdown syntax, so prose with parentheses or asterisks in
// legitimate use doesn't false-positive.
const UNSUPPORTED_MARKDOWN: Array<{ code: string; re: RegExp }> = [
  // [text](url) links — rendered literally.
  { code: "link", re: /(?<!!)\[[^\]\n]+\]\([^)\n]+\)/ },
  // Ordered lists: a line starting `1. ` / `12. ` — rendered as a paragraph.
  { code: "ordered_list", re: /^\s*\d+\.\s+\S/m },
  // Single-asterisk / underscore emphasis — rendered literally. Requires the
  // marker to hug a non-space char on both ends, and the asterisk variant
  // must not be part of a ** pair.
  {
    code: "italic",
    re: /(?<!\*)\*(?!\*)\S[^*\n]*\S\*(?!\*)|\b_\S[^_\n]*\S_\b/,
  },
  // Fenced or indented-looking code — rendered literally.
  { code: "code_fence", re: /^```/m },
  // Images ![alt](url) — rendered literally.
  { code: "image", re: /!\[[^\]\n]*\]\([^)\n]+\)/ },
  // Tables — a | delimiter row.
  { code: "table", re: /^\s*\|.+\|\s*$/m },
];

export function findUnsupportedMarkdown(body: string): string[] {
  return UNSUPPORTED_MARKDOWN.filter(({ re }) => re.test(body)).map(
    ({ code }) => code,
  );
}

// Every media key referenced by a markdown body — powers referential
// validation (import warns, publish hard-fails on a dangling reference).
export function extractMediaKeys(md: string): string[] {
  const keys = new Set<string>();
  for (const line of (md ?? "").split("\n")) {
    const m = line.trim().match(IMAGE_RE);
    if (m) keys.add(m[2]!);
  }
  return [...keys];
}
